(function () {
  const STORAGE_KEY = 'waycel-cart-sidebar-open';
  const DEFAULT_FREE_SHIPPING_THRESHOLD = 5500;
  const MOBILE_BREAKPOINT = 990;
  const SWIPE_CLOSE_MIN_DISTANCE = 80;
  const SWIPE_CLOSE_DISTANCE_RATIO = 0.22;
  const VARIANT_CONFIRMED_PROPERTY = '_waycel_variant_confirmed';
  const VARIANT_CONFIRMED_ID_PROPERTY = '_waycel_confirmed_variant_id';

  let sidebarRoot;
  let isInitialized = false;
  let refreshPromise = null;
  let fetchPatched = false;
  let documentQuantityEventsBound = false;
  let cartTriggerEventsBound = false;
  let swipeEventsBound = false;
  let isInternalCartMutation = false;
  let isVariantUpdating = false;
  let isRecommendationAdding = false;
  let swipeStartX = 0;
  let swipeStartY = 0;
  let swipeCurrentX = 0;
  let swipeTracking = false;
  let swipeHorizontal = false;
  let viewportHeightRaf = 0;
  let progressWasUnlocked = null;
  let progressUnlockAnimationTimer = 0;
  const QUANTITY_UPDATE_DEBOUNCE_MS = 500;
  const activeQuantityUpdates = new Set();
  const quantityUpdateStates = new Map();

  function setViewportHeightProperty() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
    if (!viewportHeight) return;
    document.documentElement.style.setProperty('--waycel-cart-vh', `${viewportHeight * 0.01}px`);
  }

  function scheduleViewportHeightUpdate() {
    window.cancelAnimationFrame(viewportHeightRaf);
    viewportHeightRaf = window.requestAnimationFrame(setViewportHeightProperty);
  }

  function isDesktop() {
    return window.innerWidth >= MOBILE_BREAKPOINT;
  }

  function getDrawer() {
    return sidebarRoot?.querySelector('#CartDrawer');
  }

  function getThresholdCents() {
    return Number(getDrawer()?.dataset.freeShippingThreshold) || DEFAULT_FREE_SHIPPING_THRESHOLD;
  }

  function getShippingCopySettings() {
    const drawer = getDrawer();
    return {
      pendingPrefix: drawer?.dataset.shippingPendingPrefix || 'Add',
      pendingSuffix: drawer?.dataset.shippingPendingSuffix || 'more to unlock FREE SHIPPING',
      unlockedText: drawer?.dataset.shippingUnlockedText || 'FREE SHIPPING unlocked',
    };
  }

  function findCartLineByKey(cart, lineItemKey) {
    if (!cart?.items?.length || !lineItemKey) return 0;
    const lineIndex = cart.items.findIndex((item) => item.key === lineItemKey);
    return lineIndex >= 0 ? lineIndex + 1 : 0;
  }

  function getCartChangeUrl() {
    const url = window.routes?.cart_change_url || '/cart/change';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  function getCartAddUrl() {
    const url = window.routes?.cart_add_url || '/cart/add';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  function getCartJsonUrl() {
    const url = window.routes?.cart_url || '/cart';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  function getCartUpdateUrl() {
    const root = window.Shopify?.routes?.root;
    if (root) return `${root}${root.endsWith('/') ? '' : '/'}cart/update.js`;
    return '/cart/update.js';
  }

  function getCartDrawerSectionId() {
    return getDrawer()?.dataset.sectionId || 'cart-drawer';
  }

  function getCartDrawerSectionUrl() {
    const separator = window.location.search ? '&' : '?';
    return `${window.location.pathname}${window.location.search}${separator}section_id=${encodeURIComponent(getCartDrawerSectionId())}`;
  }

  function getHeaderValue(headers, headerName) {
    if (!headers || !headerName) return '';
    if (typeof headers.get === 'function') return headers.get(headerName) || '';
    if (Array.isArray(headers)) {
      const entry = headers.find(([key]) => String(key).toLowerCase() === String(headerName).toLowerCase());
      return entry?.[1] || '';
    }
    const matchedHeaderKey = Object.keys(headers).find((key) => key.toLowerCase() === String(headerName).toLowerCase());
    return matchedHeaderKey ? headers[matchedHeaderKey] || '' : '';
  }

  function getStoredOpenState() {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  function setStoredOpenState(isOpen) {
    localStorage.setItem(STORAGE_KEY, isOpen ? 'true' : 'false');
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;
    const activeCurrency = currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: activeCurrency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function normalizeLineItemProperties(properties) {
    return Object.entries(properties || {}).reduce((normalizedProperties, [key, value]) => {
      if (!key || value == null) return normalizedProperties;
      normalizedProperties[key] = String(value);
      return normalizedProperties;
    }, {});
  }

  function getSellingPlanId(item) {
    return String(item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan || '');
  }

  function getComparableProperties(properties) {
    return JSON.stringify(
      Object.entries(normalizeLineItemProperties(properties))
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    );
  }

  function createCartLineSnapshot(item, line) {
    if (!item) return null;
    return {
      line: Number(line || 0),
      key: String(item.key || ''),
      variantId: Number(item.variant_id || item.id || 0),
      quantity: Math.max(Number(item.quantity || 1), 1),
      properties: normalizeLineItemProperties(item.properties),
      comparableProperties: getComparableProperties(item.properties),
      sellingPlanId: getSellingPlanId(item),
    };
  }

  function findCartLineBySnapshot(cart, snapshot) {
    if (!cart?.items?.length || !snapshot) return 0;
    if (snapshot.key) {
      const keyMatch = findCartLineByKey(cart, snapshot.key);
      if (keyMatch) return keyMatch;
    }

    const matchingIndex = cart.items.findIndex((item) => (
      Number(item.variant_id || item.id || 0) === snapshot.variantId &&
      getComparableProperties(item.properties) === snapshot.comparableProperties &&
      getSellingPlanId(item) === snapshot.sellingPlanId
    ));

    if (matchingIndex >= 0) return matchingIndex + 1;
    return 0;
  }

  function findConfirmedVariantItem(cart, variantId) {
    const activeVariantId = Number(variantId || 0);
    if (!cart?.items?.length || !activeVariantId) return null;

    return cart.items.find((item) => {
      const properties = normalizeLineItemProperties(item.properties);
      return (
        Number(item.variant_id || item.id || 0) === activeVariantId &&
        properties[VARIANT_CONFIRMED_PROPERTY] === 'true' &&
        String(properties[VARIANT_CONFIRMED_ID_PROPERTY] || '') === String(activeVariantId)
      );
    }) || null;
  }

  function buildConfirmedVariantProperties(item, variantId) {
    const properties = normalizeLineItemProperties(item?.properties);
    properties[VARIANT_CONFIRMED_PROPERTY] = 'true';
    properties[VARIANT_CONFIRMED_ID_PROPERTY] = String(variantId);
    return properties;
  }

  async function fetchCartJson() {
    const response = await fetch(getCartJsonUrl(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  function getCartItemByLine(cart, line) {
    return cart?.items?.[Number(line) - 1] || null;
  }

  function getLineKeyFromElement(element) {
    return String(
      element?.dataset.lineKey ||
      element?.dataset.lineItemKey ||
      element?.closest('.waycel-cart-sidebar__item')?.dataset.lineKey ||
      element?.closest('.waycel-cart-sidebar__item')?.dataset.lineItemKey ||
      ''
    );
  }

  function getPendingQuantityKey(line, lineKey) {
    return lineKey || (line ? `line:${line}` : '');
  }

  function getQuantityUpdateState(line, lineKey = '') {
    const stateKey = getPendingQuantityKey(line, lineKey);
    if (!stateKey) return null;

    let state = quantityUpdateStates.get(stateKey);
    if (!state) {
      state = {
        key: stateKey,
        line: Number(line || 0),
        lineKey: String(lineKey || ''),
        confirmedQuantity: null,
        desiredQuantity: null,
        debounceTimer: 0,
        isUpdating: false,
        needsResync: false,
        promise: null,
      };
      quantityUpdateStates.set(stateKey, state);
    }

    if (line) state.line = Number(line);
    if (lineKey) state.lineKey = String(lineKey);
    return state;
  }

  function getExistingQuantityUpdateState(line, lineKey = '') {
    const keyedState = lineKey ? quantityUpdateStates.get(String(lineKey)) : null;
    if (keyedState) return keyedState;
    const lineStateKey = line ? `line:${line}` : '';
    return lineStateKey ? quantityUpdateStates.get(lineStateKey) || null : null;
  }

  function rekeyQuantityUpdateState(state, line, lineKey = '') {
    if (!state) return null;
    const nextKey = getPendingQuantityKey(line, lineKey);
    if (!nextKey || state.key === nextKey) {
      if (line) state.line = Number(line);
      if (lineKey) state.lineKey = String(lineKey);
      return state;
    }
    quantityUpdateStates.delete(state.key);
    activeQuantityUpdates.delete(state.key);
    state.key = nextKey;
    state.line = Number(line || state.line || 0);
    state.lineKey = String(lineKey || state.lineKey || '');
    quantityUpdateStates.set(nextKey, state);
    if (state.isUpdating || state.debounceTimer || state.needsResync) {
      activeQuantityUpdates.add(nextKey);
    }
    return state;
  }

  function isQuantityStatePending(state) {
    return Boolean(state?.debounceTimer || state?.isUpdating || state?.needsResync);
  }

  function hasPendingQuantityUpdates() {
    return Array.from(quantityUpdateStates.values()).some(isQuantityStatePending);
  }

  function clearQuantityUpdateState(state) {
    if (!state) return;
    if (state.debounceTimer) {
      window.clearTimeout(state.debounceTimer);
      state.debounceTimer = 0;
    }
    activeQuantityUpdates.delete(state.key);
    quantityUpdateStates.delete(state.key);
  }

  function sleep(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function getArmSleeveSizeChartSrc() {
    return getDrawer()?.dataset.armSleeveSizeChartSrc || '';
  }

  function isArmSleeveCartItem(item) {
    const searchableText = [item.product_title, item.title, item.product_type, item.handle, item.url]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchableText.includes('arm sleeve') || searchableText.includes('arm-sleeve') || searchableText.includes('arm_sleeve');
  }

  function buildShippingMessageMarkup(remaining, currency) {
    const shippingCopy = getShippingCopySettings();
    if (remaining > 0) {
      return `
        <span>${escapeHtml(shippingCopy.pendingPrefix)}</span>
        <span class="waycel-cart-sidebar__shipping-emphasis"><strong>${escapeHtml(formatMoney(remaining, currency))}</strong></span>
        <span>${escapeHtml(shippingCopy.pendingSuffix)}</span>
      `;
    }
    return `<span><strong>${escapeHtml(shippingCopy.unlockedText)}</strong></span>`;
  }

  function triggerProgressUnlockAnimation(shippingProgressBar) {
    if (!shippingProgressBar) return;
    window.clearTimeout(progressUnlockAnimationTimer);
    shippingProgressBar.classList.remove('just-unlocked');
    shippingProgressBar.offsetHeight;
    shippingProgressBar.classList.add('just-unlocked');
    progressUnlockAnimationTimer = window.setTimeout(() => {
      shippingProgressBar.classList.remove('just-unlocked');
    }, 700);
  }

  function updateOpenClasses(isOpen) {
    if (!sidebarRoot) return;
    scheduleViewportHeightUpdate();
    sidebarRoot.classList.toggle('active', isOpen);
    sidebarRoot.classList.toggle('waycel-is-open', isOpen);
    document.documentElement.classList.toggle('waycel-cart-open', isOpen);
    if (isDesktop()) {
      document.body.classList.remove('overflow-hidden');
    } else {
      document.body.classList.toggle('overflow-hidden', isOpen);
    }
  }

  function forceCloseSidebar() {
    setStoredOpenState(false);
    updateOpenClasses(false);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(sidebarRoot.activeElement);
  }

  function openSidebar(triggeredBy) {
    if (!sidebarRoot) return;
    if (triggeredBy && typeof sidebarRoot.setActiveElement === 'function') {
      sidebarRoot.setActiveElement(triggeredBy);
    }
    setStoredOpenState(true);
    updateOpenClasses(true);
    if (!isDesktop() && typeof trapFocus === 'function') {
      const focusTarget = sidebarRoot.querySelector('[data-waycel-cart-close]');
      if (focusTarget) {
        requestAnimationFrame(() => trapFocus(getDrawer(), focusTarget));
      }
    }
  }

  function buildCartItemMarkup(item, index, currency) {
    const itemTitle = escapeHtml(item.product_title || item.title);
    const itemUrl = escapeAttribute(item.url || '#');
    const showSizeChart = isArmSleeveCartItem(item) && Boolean(getArmSleeveSizeChartSrc());
    const imageMarkup = item.featured_image?.url
      ? `<img class="waycel-cart-sidebar__item-image" src="${escapeAttribute(item.featured_image.url)}" alt="${escapeAttribute(item.featured_image.alt || itemTitle)}" loading="lazy" width="96" height="96">`
      : '';

    return `
      <article class="waycel-cart-sidebar__item" data-line="${index + 1}" data-line-key="${escapeAttribute(item.key || '')}">
        <a href="${itemUrl}" class="waycel-cart-sidebar__item-image-link" aria-hidden="true" tabindex="-1">
          ${imageMarkup}
        </a>
        <div class="waycel-cart-sidebar__item-content">
          <div class="waycel-cart-sidebar__item-top">
            <div>
              <a href="${itemUrl}" class="waycel-cart-sidebar__item-title">${itemTitle}</a>
              ${showSizeChart ? '<button type="button" class="waycel-cart-sidebar__size-chart-link" data-waycel-size-chart-open>View size chart</button>' : ''}
            </div>
            <button type="button" class="waycel-cart-sidebar__remove" data-waycel-remove data-line="${index + 1}" data-line-key="${escapeAttribute(item.key || '')}" aria-label="Remove item">
              <svg class="waycel-cart-sidebar__remove-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path class="waycel-cart-sidebar__remove-lid" d="M7.4 5.2h5.2M8.8 5.2V4h2.4v1.2M4.8 6.9h10.4"/>
                <path class="waycel-cart-sidebar__remove-can" d="M6.2 8.4l.5 6.5c.1.9.8 1.5 1.7 1.5h3.2c.9 0 1.6-.6 1.7-1.5l.5-6.5M8.7 10.2v4M11.3 10.2v4"/>
              </svg>
            </button>
          </div>
          <div class="waycel-cart-sidebar__item-bottom">
            <div class="waycel-cart-sidebar__price-group" data-waycel-variant-compare-price="${item.original_price > item.final_price ? item.original_price : 0}">
              <span class="waycel-cart-sidebar__price">${formatMoney(item.final_price, currency)}</span>
              ${item.original_price > item.final_price ? `<s class="waycel-cart-sidebar__compare">${formatMoney(item.original_price, currency)}</s>` : ''}
            </div>
            <div class="waycel-cart-sidebar__quantity" aria-label="Quantity">
              <button type="button" data-waycel-qty-button="minus" data-line="${index + 1}" data-line-key="${escapeAttribute(item.key || '')}">-</button>
              <span class="waycel-cart-sidebar__quantity-value">${item.quantity}</span>
              <button type="button" data-waycel-qty-button="plus" data-line="${index + 1}" data-line-key="${escapeAttribute(item.key || '')}">+</button>
            </div>
          </div>
        </div>
      </article>
    `;
  }

  function getVariantSelects() {
    return Array.from(sidebarRoot?.querySelectorAll('[data-waycel-variant-select]') || []);
  }

  function getVariantSelectLineKey(variantSelect) {
    return getLineKeyFromElement(variantSelect);
  }

  function getCartItemVisualIndex(itemCard) {
    const items = Array.from(sidebarRoot?.querySelectorAll('[data-waycel-cart-items] .waycel-cart-sidebar__item') || []);
    const index = items.indexOf(itemCard);
    return index >= 0 ? index : 0;
  }

  function getVisibleLineQuantity(itemCard, fallbackQuantity = 1) {
    const quantityElement = itemCard?.querySelector('.waycel-cart-sidebar__quantity-value');
    const visibleQuantity = Number(quantityElement?.textContent?.trim() || 0);
    if (Number.isFinite(visibleQuantity) && visibleQuantity > 0) return visibleQuantity;

    const variantSelectQuantity = Number(itemCard?.querySelector('[data-waycel-variant-select]')?.dataset.quantity || 0);
    if (Number.isFinite(variantSelectQuantity) && variantSelectQuantity > 0) return variantSelectQuantity;

    const fallback = Number(fallbackQuantity || 0);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : 1;
  }

  function setCartLineControlsDisabled(itemCard, isDisabled) {
    itemCard
      ?.querySelectorAll('[data-waycel-variant-select], [data-waycel-qty-button], [data-waycel-remove]')
      .forEach((control) => {
        if (isDisabled) {
          if (control.disabled) return;
          control.dataset.waycelLineLockDisabled = 'true';
          control.disabled = true;
          return;
        }

        if (control.dataset.waycelLineLockDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.waycelLineLockDisabled;
        }
      });
  }

  function setCartLineQuantityPending(itemCard, isPending) {
    itemCard?.toggleAttribute('data-waycel-quantity-syncing', isPending);
    itemCard
      ?.querySelectorAll('[data-waycel-variant-select], [data-waycel-remove]')
      .forEach((control) => {
        if (isPending) {
          if (control.disabled) return;
          control.dataset.waycelQuantityLockDisabled = 'true';
          control.disabled = true;
          return;
        }

        if (control.dataset.waycelQuantityLockDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.waycelQuantityLockDisabled;
        }
      });
  }

  function setVisibleLineQuantity(itemCard, quantity) {
    const quantityElement = itemCard?.querySelector('.waycel-cart-sidebar__quantity-value');
    const safeQuantity = Math.max(Number(quantity || 0), 0);
    if (quantityElement) quantityElement.textContent = safeQuantity;
    updateQuantityMinusButtonState(itemCard, safeQuantity);
  }

  function getCartItemForVariantSelect(cart, line, variantSelect) {
    const lineKey = getVariantSelectLineKey(variantSelect);
    if (lineKey) {
      const itemByKey = cart?.items?.find((item) => item.key === lineKey);
      if (itemByKey) return itemByKey;
    }

    return getCartItemByLine(cart, line);
  }

  function hydrateVariantSelectsFromCartItems(cartItems = []) {
    cartItems.forEach((item, index) => {
      const itemRow =
        (item.key ? Array.from(sidebarRoot?.querySelectorAll('.waycel-cart-sidebar__item') || []).find((row) => row.dataset.lineKey === String(item.key)) : null) ||
        sidebarRoot?.querySelector(`.waycel-cart-sidebar__item[data-line="${index + 1}"]`);
      const variantSelect = itemRow?.querySelector('[data-waycel-variant-select]');
      const lineKey = String(item.key || '');
      if (item.key) {
        if (itemRow) itemRow.dataset.line = String(index + 1);
        if (itemRow) itemRow.dataset.lineKey = lineKey;
        itemRow?.querySelectorAll('[data-line]').forEach((control) => {
          control.dataset.line = String(index + 1);
          control.dataset.lineKey = lineKey;
        });
        if (variantSelect) variantSelect.dataset.lineKey = lineKey;
      }
      if (variantSelect && item.variant_id) {
        variantSelect.dataset.currentVariantId = String(item.variant_id);
      }
    });
  }

  function setVariantUpdatingState(isUpdating) {
    if (!sidebarRoot) return;
    sidebarRoot.classList.toggle('waycel-cart-sidebar--variant-updating', isUpdating);
    sidebarRoot
      .querySelectorAll('[data-waycel-variant-select], [data-waycel-qty-button], [data-waycel-remove], [data-waycel-recommendation-add]')
      .forEach((control) => {
        if (isUpdating) {
          if (control.disabled) return;
          control.dataset.waycelVariantLockDisabled = 'true';
          control.disabled = true;
          return;
        }
        if (control.dataset.waycelVariantLockDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.waycelVariantLockDisabled;
        }
      });
  }

  function markVariantSelectConfirmed(variantSelect, variantId) {
    if (!variantSelect) return;
    const variantSelectWrap = variantSelect.closest('.waycel-cart-sidebar__variant-select-wrap');
    variantSelect.dataset.waycelVariantConfirmed = 'true';
    if (variantId) variantSelect.value = String(variantId);
    variantSelect.classList.remove('waycel-cart-sidebar__variant-select--needs-confirmation');
    variantSelectWrap?.classList.remove('waycel-cart-sidebar__variant-select-wrap--needs-confirmation');
  }

  function markVariantSelectNeedsConfirmation(variantSelect) {
    if (!variantSelect) return;
    const variantSelectWrap = variantSelect.closest('.waycel-cart-sidebar__variant-select-wrap');
    variantSelect.dataset.waycelVariantConfirmed = 'false';
    variantSelect.classList.add('waycel-cart-sidebar__variant-select--needs-confirmation');
    variantSelectWrap?.classList.add('waycel-cart-sidebar__variant-select-wrap--needs-confirmation');
  }

  function syncVariantSelectionState() {
    getVariantSelects().forEach((variantSelect) => {
      const currentVariantId = String(variantSelect.dataset.currentVariantId || '');
      if (variantSelect.dataset.waycelVariantConfirmed === 'true' && currentVariantId && !variantSelect.value) {
        variantSelect.value = currentVariantId;
      }
      const selectedVariant = String(variantSelect.value || '');
      const isConfirmed =
        variantSelect.dataset.waycelVariantConfirmed === 'true' &&
        Boolean(selectedVariant) &&
        (!currentVariantId || selectedVariant === currentVariantId);
      const variantSelectWrap = variantSelect.closest('.waycel-cart-sidebar__variant-select-wrap');
      variantSelect.dataset.waycelVariantConfirmed = isConfirmed ? 'true' : 'false';
      variantSelect.classList.toggle('waycel-cart-sidebar__variant-select--needs-confirmation', !isConfirmed);
      variantSelectWrap?.classList.toggle('waycel-cart-sidebar__variant-select-wrap--needs-confirmation', !isConfirmed);
    });
  }

  function getVariantCheckoutCopy() {
    return {
      button: 'Checkout',
      warning: 'Please select all required options before checkout',
      quantityWarning: 'Updating cart. Please wait.',
    };
  }

  function updateCheckoutVariantValidation() {
    if (!sidebarRoot) return true;
    syncVariantSelectionState();
    const selectors = getVariantSelects();
    const unconfirmedSelectors = selectors.filter((s) => s.dataset.waycelVariantConfirmed !== 'true');
    const isQuantityPending = hasPendingQuantityUpdates();
    const isBlocked = unconfirmedSelectors.length > 0 || isQuantityPending;
    const checkoutButton = sidebarRoot.querySelector('[data-waycel-checkout-button]');
    const paymentOptionsTrigger = sidebarRoot.querySelector('[data-waycel-more-payment-options]');
    const checkoutWarning = sidebarRoot.querySelector('[data-waycel-variant-warning]');
    const checkoutCopy = getVariantCheckoutCopy();
    if (checkoutButton) {
      checkoutButton.disabled = unconfirmedSelectors.length > 0;
      checkoutButton.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
      checkoutButton.classList.toggle('waycel-cart-sidebar__checkout--blocked', isBlocked);
      checkoutButton.textContent = checkoutCopy.button;
    }
    if (paymentOptionsTrigger) {
      paymentOptionsTrigger.disabled = isQuantityPending;
      paymentOptionsTrigger.setAttribute('aria-disabled', isQuantityPending ? 'true' : 'false');
      if (isQuantityPending && paymentOptionsTrigger.getAttribute('aria-expanded') === 'true') {
        setPaymentOptionsExpanded(paymentOptionsTrigger, false);
      }
    }
    if (checkoutWarning) {
      checkoutWarning.hidden = !isBlocked;
      checkoutWarning.textContent = isQuantityPending ? checkoutCopy.quantityWarning : checkoutCopy.warning;
    }
    sidebarRoot.classList.toggle('waycel-cart-sidebar--variant-required', isBlocked);
    return !isBlocked;
  }

  function updateQuantityMinusButtonState(itemCard, quantity) {
    const minusButton = itemCard?.querySelector('[data-waycel-qty-button="minus"]');
    if (!minusButton) return;
    const isMinimumQuantity = Number(quantity || 0) <= 1;
    minusButton.classList.toggle('is-disabled', isMinimumQuantity);
    minusButton.disabled = isMinimumQuantity;
    minusButton.setAttribute('aria-disabled', isMinimumQuantity ? 'true' : 'false');
  }

  function syncQuantityMinusButtonStates() {
    sidebarRoot?.querySelectorAll('.waycel-cart-sidebar__item').forEach((itemCard) => {
      const quantityElement = itemCard.querySelector('.waycel-cart-sidebar__quantity-value');
      updateQuantityMinusButtonState(itemCard, Number(quantityElement?.textContent?.trim() || 0));
    });
  }

  function syncRenderedCartItems(cart) {
    const itemsContainer = sidebarRoot?.querySelector('[data-waycel-cart-items]');
    if (!itemsContainer) return;
    cart.items.forEach((item, index) => {
      const itemRow =
        (item.key ? Array.from(itemsContainer.querySelectorAll('.waycel-cart-sidebar__item')).find((row) => row.dataset.lineKey === item.key) : null) ||
        itemsContainer.querySelector(`.waycel-cart-sidebar__item[data-line="${index + 1}"]`);
      if (!itemRow) return;
      const line = String(index + 1);
      const lineKey = String(item.key || '');
      itemRow.dataset.line = line;
      itemRow.dataset.lineKey = lineKey;
      itemRow.querySelectorAll('[data-line]').forEach((control) => {
        control.dataset.line = line;
        control.dataset.lineKey = lineKey;
      });
      const quantityElement = itemRow.querySelector('.waycel-cart-sidebar__quantity-value');
      const priceGroupElement = itemRow.querySelector('.waycel-cart-sidebar__price-group');
      const priceElement = itemRow.querySelector('.waycel-cart-sidebar__price');
      const compareElement = itemRow.querySelector('.waycel-cart-sidebar__compare');
      const variantComparePrice = Number(priceGroupElement?.dataset.waycelVariantComparePrice || 0);
      const lineComparePrice = item.original_price > item.final_price ? item.original_price : 0;
      const comparePrice = variantComparePrice > item.final_price ? variantComparePrice : lineComparePrice;
      const quantityState = getExistingQuantityUpdateState(index + 1, lineKey);
      if (quantityState) {
        rekeyQuantityUpdateState(quantityState, index + 1, lineKey);
        quantityState.confirmedQuantity = Number(item.quantity || 0);
      }
      const renderedQuantity = isQuantityStatePending(quantityState) && Number.isFinite(Number(quantityState.desiredQuantity))
        ? Number(quantityState.desiredQuantity)
        : item.quantity;
      if (quantityElement) quantityElement.textContent = renderedQuantity;
      updateQuantityMinusButtonState(itemRow, renderedQuantity);
      setCartLineQuantityPending(itemRow, isQuantityStatePending(quantityState));
      if (priceElement) priceElement.textContent = formatMoney(item.final_price, cart.currency);
      if (comparePrice > item.final_price) {
        if (compareElement) {
          compareElement.textContent = formatMoney(comparePrice, cart.currency);
        } else if (priceElement?.parentElement) {
          priceElement.insertAdjacentHTML('afterend', `<s class="waycel-cart-sidebar__compare">${formatMoney(comparePrice, cart.currency)}</s>`);
        }
      } else {
        compareElement?.remove();
      }
    });
  }

  function getCartScrollState() {
    const itemsContainer = sidebarRoot?.querySelector('[data-waycel-cart-items]');
    return { itemsScrollTop: itemsContainer?.scrollTop || 0 };
  }

  function restoreCartScrollState(scrollState) {
    if (!scrollState || !sidebarRoot) return;
    const restore = () => {
      const itemsContainer = sidebarRoot?.querySelector('[data-waycel-cart-items]');
      if (itemsContainer) {
        itemsContainer.scrollTop = Math.min(scrollState.itemsScrollTop, itemsContainer.scrollHeight);
      }
    };
    restore();
    window.requestAnimationFrame(restore);
  }

  function getPaymentOptionsPanel(trigger) {
    if (!trigger) return null;
    const panelId = trigger.getAttribute('aria-controls');
    return (
      (panelId ? document.getElementById(panelId) : null) ||
      trigger.closest('[data-waycel-cart-ctas]')?.querySelector('[data-waycel-payment-options-panel]') ||
      null
    );
  }

  function updatePaymentOptionsTriggerText(trigger, isExpanded) {
    if (!trigger) return;
    trigger.textContent = isExpanded ? 'Show less' : 'More payment options';
  }

  function setPaymentOptionsExpanded(trigger, isExpanded) {
    const panel = getPaymentOptionsPanel(trigger);
    if (!trigger || !panel) return;
    scheduleViewportHeightUpdate();
    trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    updatePaymentOptionsTriggerText(trigger, isExpanded);
    trigger.classList.toggle('is-open', isExpanded);
    panel.classList.toggle('is-open', isExpanded);
    panel.hidden = !isExpanded;
    if (isExpanded) {
      window.requestAnimationFrame(() => {
        const walletScroll = panel.querySelector('[data-waycel-wallet-scroll]');
        if (walletScroll) walletScroll.scrollTop = 0;
      });
    }
  }

  function togglePaymentOptions(trigger) {
    const isExpanded = trigger?.getAttribute('aria-expanded') === 'true';
    setPaymentOptionsExpanded(trigger, !isExpanded);
  }

  function getAcceleratedCheckoutContainer(root = sidebarRoot) {
    if (!root) return null;
    if (root.matches?.('[data-waycel-accelerated-checkout]')) return root;
    return root.querySelector?.('[data-waycel-accelerated-checkout]') || null;
  }

  function isBuyWithPrimeElement(element) {
    if (!element) return false;
    const className = typeof element.className === 'string' ? element.className : '';
    const searchableValue = `${element.id || ''} ${className}`.toLowerCase();
    return searchableValue.includes('buy-with-prime') || searchableValue.includes('buywithprime');
  }

  function isPreservableAcceleratedCheckoutElement(element) {
    return Boolean(
      element?.matches?.('shopify-accelerated-checkout-cart, shopify-accelerated-checkout') ||
      isBuyWithPrimeElement(element)
    );
  }

  function getPreservableAcceleratedCheckoutElements(root = sidebarRoot) {
    const container = getAcceleratedCheckoutContainer(root);
    return Array.from(container?.children || []).filter(isPreservableAcceleratedCheckoutElement);
  }

  function getAcceleratedCheckoutElement(root = sidebarRoot) {
    const container = getAcceleratedCheckoutContainer(root) || root;
    return container?.querySelector?.('shopify-accelerated-checkout-cart, shopify-accelerated-checkout') || null;
  }

  function findMatchingPreservedAcceleratedElement(container, element) {
    if (!container || !element) return null;
    if (element.matches?.('shopify-accelerated-checkout-cart, shopify-accelerated-checkout')) {
      return container.querySelector(element.tagName.toLowerCase());
    }
    if (element.id) {
      return Array.from(container.querySelectorAll('[id]')).find((candidate) => candidate.id === element.id) || null;
    }
    if (isBuyWithPrimeElement(element)) {
      return Array.from(container.children).find(isBuyWithPrimeElement) || null;
    }
    return null;
  }

  function restoreAcceleratedCheckoutElements(acceleratedCheckoutElements) {
    if (!acceleratedCheckoutElements?.length || !sidebarRoot) return;
    const targetContainer = getAcceleratedCheckoutContainer(sidebarRoot);
    if (!targetContainer) return;
    acceleratedCheckoutElements.forEach((element) => {
      if (!element || targetContainer.contains(element)) return;
      const matchingElement = findMatchingPreservedAcceleratedElement(targetContainer, element);
      if (matchingElement) {
        matchingElement.replaceWith(element);
      } else {
        targetContainer.appendChild(element);
      }
    });
  }

  function restoreAcceleratedCheckoutElement(acceleratedCheckoutElement) {
    restoreAcceleratedCheckoutElements(acceleratedCheckoutElement ? [acceleratedCheckoutElement] : []);
  }

  function replaceChildrenPreservingAcceleratedCheckout(target, source) {
    if (!target || !source) return;
    const existingAcceleratedCheckoutElements = getPreservableAcceleratedCheckoutElements(target);
    const existingPaymentOptionsTrigger = target.querySelector('[data-waycel-more-payment-options]');
    const wasPaymentOptionsExpanded = existingPaymentOptionsTrigger?.getAttribute('aria-expanded') === 'true';
    const sourceAcceleratedCheckoutContainer = getAcceleratedCheckoutContainer(source);

    if (existingAcceleratedCheckoutElements.length && !sourceAcceleratedCheckoutContainer) return;

    if (sourceAcceleratedCheckoutContainer) {
      existingAcceleratedCheckoutElements.forEach((element) => {
        const sourceMatch = findMatchingPreservedAcceleratedElement(sourceAcceleratedCheckoutContainer, element);
        if (sourceMatch) {
          sourceMatch.replaceWith(element);
        } else {
          sourceAcceleratedCheckoutContainer.appendChild(element);
        }
      });
    }

    target.replaceChildren(...Array.from(source.childNodes));

    const nextPaymentOptionsTrigger = target.querySelector('[data-waycel-more-payment-options]');
    if (nextPaymentOptionsTrigger) {
      setPaymentOptionsExpanded(nextPaymentOptionsTrigger, wasPaymentOptionsExpanded);
    }
  }

  function refreshCartCtasFromSection(html) {
    if (!sidebarRoot || !html) return;
    const target = sidebarRoot.querySelector('[data-waycel-cart-ctas]');
    const source = html.querySelector('[data-waycel-cart-ctas]');
    replaceChildrenPreservingAcceleratedCheckout(target, source);
  }

  function refreshAppliedDiscountsFromSection(html) {
    if (!sidebarRoot || !html) return;
    const target = sidebarRoot.querySelector('[data-waycel-applied-discounts]');
    const source = html.querySelector('[data-waycel-applied-discounts]');
    if (target && source) {
      target.replaceWith(source);
      return;
    }
    if (!target && source) {
      const footer = sidebarRoot.querySelector('[data-waycel-cart-footer]');
      const subtotal = footer?.querySelector('.waycel-cart-sidebar__subtotal');
      if (subtotal) {
        subtotal.before(source);
      } else {
        footer?.appendChild(source);
      }
      return;
    }
    if (target && !source) {
      target.remove();
    }
  }

  function getCartDiscountTitles(cart) {
    const titles = [];
    const seenTitles = new Set();
    const addTitle = (title) => {
      const normalizedTitle = String(title || '').trim();
      if (!normalizedTitle || seenTitles.has(normalizedTitle)) return;
      seenTitles.add(normalizedTitle);
      titles.push(normalizedTitle);
    };

    (cart?.cart_level_discount_applications || []).forEach((discount) => addTitle(discount.title || discount.code));
    (cart?.discount_applications || []).forEach((discount) => addTitle(discount.title || discount.code));
    (cart?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => addTitle(allocation.discount_application?.title));
      (item.discount_allocations || []).forEach((allocation) => addTitle(allocation.discount_application?.title || allocation.title));
      (item.discounts || []).forEach((discount) => addTitle(discount.title || discount.code));
    });

    return titles;
  }

  function buildAppliedDiscountsMarkup(cart) {
    const totalDiscount = Number(cart?.total_discount || 0);
    if (totalDiscount <= 0) return '';
    const discountTitles = getCartDiscountTitles(cart);
    const discountItems = discountTitles.length
      ? discountTitles.map((title) => `
          <li class="waycel-cart-sidebar__applied-discount">
            <span class="waycel-cart-sidebar__applied-discount-title">${escapeHtml(title)}</span>
          </li>
        `).join('')
      : `
          <li class="waycel-cart-sidebar__applied-discount">
            <span class="waycel-cart-sidebar__applied-discount-title">Discount</span>
          </li>
        `;

    return `
      <div class="waycel-cart-sidebar__applied-discounts" data-waycel-applied-discounts>
        <span class="waycel-cart-sidebar__applied-discounts-title">Discounts</span>
        <ul class="waycel-cart-sidebar__applied-discounts-list">${discountItems}</ul>
        <div class="waycel-cart-sidebar__applied-discount-total">
          <strong>-${formatMoney(totalDiscount, cart.currency)}</strong>
          <button type="button" class="waycel-cart-sidebar__applied-discount-remove" data-waycel-remove-discount aria-label="Remove discount">&times;</button>
        </div>
      </div>
    `;
  }

  function refreshAppliedDiscountsFromCart(cart) {
    if (!sidebarRoot) return;
    const target = sidebarRoot.querySelector('[data-waycel-applied-discounts]');
    const discountMarkup = buildAppliedDiscountsMarkup(cart);
    if (!discountMarkup) {
      target?.remove();
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = discountMarkup.trim();
    const source = template.content.firstElementChild;
    if (!source) return;

    if (target) {
      target.replaceWith(source);
      return;
    }

    const footer = sidebarRoot.querySelector('[data-waycel-cart-footer]');
    const subtotal = footer?.querySelector('.waycel-cart-sidebar__subtotal');
    if (subtotal) {
      subtotal.before(source);
    } else {
      footer?.appendChild(source);
    }
  }

  function getCartDrawerSectionHtml(cartResponse) {
    const sections = cartResponse?.sections;
    if (!sections) return '';
    const sectionId = getCartDrawerSectionId();
    return sections[sectionId] || sections['cart-drawer'] || '';
  }

  function parseCartDrawerSectionHtml(sectionHtml) {
    if (!sectionHtml) return null;
    if (typeof sectionHtml === 'string') {
      return new DOMParser().parseFromString(sectionHtml, 'text/html');
    }
    return sectionHtml;
  }

  function applyCartDrawerSectionHtml(sectionHtml, options = {}) {
    if (!sidebarRoot || !sectionHtml) return false;
    const html = parseCartDrawerSectionHtml(sectionHtml);
    if (!html) return false;
    const drawer = getDrawer();
    const sourceDrawer = html.getElementById('CartDrawer');
    const target = sidebarRoot.querySelector('[data-waycel-recommendations]');
    const itemsTarget = sidebarRoot.querySelector('[data-waycel-cart-items]');
    const itemsSource = html.querySelector('[data-waycel-cart-items]');
    const sizeModalTarget = sidebarRoot.querySelector('[data-waycel-size-chart-modal]');
    const sizeModalSource = html.querySelector('[data-waycel-size-chart-modal]');
    const source = html.querySelector('[data-waycel-recommendations]');
    const shouldUpdateRecommendations = options.updateRecommendations !== false;
    const shouldUpdateCtas = options.updateCtas !== false;

    if (drawer && sourceDrawer) {
      drawer.dataset.sectionId = sourceDrawer.dataset.sectionId || getCartDrawerSectionId();
      drawer.dataset.freeShippingThreshold = sourceDrawer.dataset.freeShippingThreshold || DEFAULT_FREE_SHIPPING_THRESHOLD;
      drawer.dataset.armSleeveSizeChartSrc = sourceDrawer.dataset.armSleeveSizeChartSrc || '';
      drawer.dataset.shippingPendingPrefix = sourceDrawer.dataset.shippingPendingPrefix || 'Add';
      drawer.dataset.shippingPendingSuffix = sourceDrawer.dataset.shippingPendingSuffix || 'more to unlock FREE SHIPPING';
      drawer.dataset.shippingUnlockedText = sourceDrawer.dataset.shippingUnlockedText || 'FREE SHIPPING unlocked';
    }
    if (options.updateItems && itemsTarget && itemsSource) {
      itemsTarget.innerHTML = itemsSource.innerHTML;
      hydrateVariantSelectsFromCartItems(options.cartItems);
    }
    if (sizeModalTarget && sizeModalSource && !sizeModalTarget.classList.contains('is-open')) {
      sizeModalTarget.innerHTML = sizeModalSource.innerHTML;
    }
    if (target && source && shouldUpdateRecommendations) {
      target.className = source.className;
      target.innerHTML = source.innerHTML;
    }
    if (shouldUpdateCtas) {
      refreshCartCtasFromSection(html);
    }
    refreshAppliedDiscountsFromSection(html);
    syncQuantityMinusButtonStates();
    updateCheckoutVariantValidation();
    return true;
  }

  function hasCartDrawerSectionHtml(cartResponse) {
    return Boolean(getCartDrawerSectionHtml(cartResponse));
  }

  function renderCart(cart, options = {}) {
    if (!sidebarRoot) return;
    const scrollState = options.preserveScroll ? getCartScrollState() : null;
    updateWaycelHeaderCartCount(cart.item_count);
    const emptyState = sidebarRoot.querySelector('[data-waycel-cart-empty]');
    const mainState = sidebarRoot.querySelector('[data-waycel-cart-main]');
    const itemsContainer = sidebarRoot.querySelector('[data-waycel-cart-items]');
    const subtotalElement = sidebarRoot.querySelector('[data-waycel-subtotal]');
    const shippingMessageElement = sidebarRoot.querySelector('[data-waycel-shipping-message]');
    const shippingProgressElement = sidebarRoot.querySelector('[data-waycel-shipping-progress]');
    const shippingProgressBar = shippingProgressElement?.closest('.waycel-cart-sidebar__shipping-bar');
    const subtotalCents = Number(cart.items_subtotal_price ?? cart.total_price ?? 0);
    const isEmpty = !cart.item_count;
    sidebarRoot.classList.toggle('is-empty', isEmpty);
    emptyState?.classList.toggle('hidden', !isEmpty);
    mainState?.classList.toggle('hidden', isEmpty);
    if (itemsContainer) {
      if (options.skipItems) {
        syncRenderedCartItems(cart);
      } else {
        itemsContainer.innerHTML = cart.items.map((item, index) => buildCartItemMarkup(item, index, cart.currency)).join('');
      }
      hydrateVariantSelectsFromCartItems(cart.items);
      syncQuantityMinusButtonStates();
    }
    if (subtotalElement) {
      subtotalElement.textContent = formatMoney(subtotalCents, cart.currency);
    }
    const threshold = getThresholdCents();
    const remaining = Math.max(threshold - subtotalCents, 0);
    const progress = Math.min((subtotalCents / threshold) * 100, 100);
    const isProgressUnlocked = subtotalCents >= threshold;
    if (shippingMessageElement) {
      shippingMessageElement.innerHTML = buildShippingMessageMarkup(remaining, cart.currency);
    }
    if (shippingProgressElement) {
      shippingProgressElement.style.width = `${progress}%`;
    }
    if (shippingProgressBar) {
      shippingProgressBar.classList.toggle('is-unlocked', isProgressUnlocked);
      if (progressWasUnlocked === false && isProgressUnlocked) {
        triggerProgressUnlockAnimation(shippingProgressBar);
      }
    }
    progressWasUnlocked = isProgressUnlocked;
    updateCheckoutVariantValidation();
    restoreCartScrollState(scrollState);
  }

  function updateWaycelHeaderCartCount(itemCount) {
    const count = Number(itemCount || 0);
    document.querySelectorAll('[class*="wh-cart-wrap-"]').forEach((cartWrap) => {
      const wrapClass = Array.from(cartWrap.classList).find((c) => c.startsWith('wh-cart-wrap-'));
      if (!wrapClass) return;
      const sectionSuffix = wrapClass.replace('wh-cart-wrap-', '');
      const badgeClass = `wh-badge-${sectionSuffix}`;
      let badge = Array.from(cartWrap.children).find((child) => child.classList?.contains(badgeClass));
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = badgeClass;
          cartWrap.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  async function refreshCartIconBubble() {
    const target = document.getElementById('cart-icon-bubble');
    if (!target || !window.routes?.cart_url) return;
    const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const source = html.getElementById('cart-icon-bubble');
    if (source) target.innerHTML = source.innerHTML;
  }

  async function refreshRecommendationsFromSection(options = {}) {
    if (!sidebarRoot) return;
    const scrollState = options.preserveScroll ? getCartScrollState() : null;
    const sectionUrl = getCartDrawerSectionUrl();
    const response = await fetch(sectionUrl, { headers: { Accept: 'text/html' } });
    const text = await response.text();
    applyCartDrawerSectionHtml(text, options);
    restoreCartScrollState(scrollState);
  }

  function refreshCart(options = {}) {
    if (refreshPromise) return refreshPromise;
    const preserveScroll = options.preserveScroll !== false;
    refreshPromise = fetch(getCartJsonUrl(), { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        renderCart(cart, { preserveScroll, skipItems: Boolean(options.updateItemsFromSection) });
        return Promise.all([
          refreshCartIconBubble().catch(() => undefined),
          refreshRecommendationsFromSection({ preserveScroll, updateItems: Boolean(options.updateItemsFromSection), cartItems: cart.items }).catch(() => undefined),
        ]);
      })
      .catch((error) => {
        console.error('Waycel cart sidebar refresh failed', error);
      })
      .finally(() => {
        refreshPromise = null;
      });
    return refreshPromise;
  }

  function findCartItemForQuantityState(cart, state, fallbackLine) {
    if (!cart?.items?.length) return null;
    if (state?.lineKey) {
      const itemByKey = cart.items.find((item) => item.key === state.lineKey);
      if (itemByKey) return itemByKey;
    }
    return getCartItemByLine(cart, fallbackLine || state?.line);
  }

  async function processQuantityUpdateState(state) {
    if (!state || state.isUpdating) return state?.promise;
    const scrollState = getCartScrollState();
    if (state.debounceTimer) {
      window.clearTimeout(state.debounceTimer);
      state.debounceTimer = 0;
    }
    const requestedQuantity = Math.max(Number(state.desiredQuantity || 0), 0);
    state.isUpdating = true;
    state.needsResync = false;
    activeQuantityUpdates.add(state.key);
    updateCheckoutVariantValidation();
    state.promise = (async () => {
      const changePayload = state.lineKey ? { id: state.lineKey, quantity: requestedQuantity } : { line: state.line, quantity: requestedQuantity };
      const requestPayload = requestedQuantity === 0
        ? { ...changePayload, sections: getCartDrawerSectionId(), sections_url: window.location.pathname }
        : changePayload;
      const response = await fetch(getCartChangeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(requestPayload),
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();
      const confirmedItem = findCartItemForQuantityState(cart, state, state.line);
      const confirmedQuantity = Number(confirmedItem?.quantity ?? 0);
      const desiredChangedDuringRequest = Number(state.desiredQuantity) !== requestedQuantity;
      const shouldRefreshSection = requestedQuantity === 0;

      state.confirmedQuantity = Number.isFinite(confirmedQuantity) ? confirmedQuantity : 0;
      if (confirmedItem?.key) rekeyQuantityUpdateState(state, findCartLineByKey(cart, confirmedItem.key), confirmedItem.key);
      if (!desiredChangedDuringRequest && state.confirmedQuantity !== requestedQuantity) {
        console.warn('Waycel cart: Shopify confirmed a different quantity', {
          requested: requestedQuantity,
          confirmed: state.confirmedQuantity,
          lineKey: state.lineKey,
          line: state.line,
        });
        state.desiredQuantity = state.confirmedQuantity;
      }

      renderCart(cart, { preserveScroll: true, skipItems: true });
      refreshAppliedDiscountsFromCart(cart);
      const postUpdateTasks = [refreshCartIconBubble().catch(() => undefined)];
      if (shouldRefreshSection) {
        const cartDrawerSectionHtml = getCartDrawerSectionHtml(cart);
        const sectionOptions = { preserveScroll: true, updateItems: true, updateRecommendations: true, updateCtas: true, cartItems: cart.items };
        if (cartDrawerSectionHtml) {
          applyCartDrawerSectionHtml(cartDrawerSectionHtml, sectionOptions);
        } else {
          postUpdateTasks.push(refreshRecommendationsFromSection(sectionOptions).catch(() => undefined));
        }
      }
      await Promise.all(postUpdateTasks);
      restoreCartScrollState(scrollState);
      return cart;
    })();

    try {
      return await state.promise;
    } catch (error) {
      console.error('Waycel cart sidebar change failed', error);
      state.desiredQuantity = Number.isFinite(Number(state.confirmedQuantity)) ? Number(state.confirmedQuantity) : null;
    } finally {
      state.isUpdating = false;
      state.promise = null;
      activeQuantityUpdates.delete(state.key);

      if (state.desiredQuantity != null && Number.isFinite(Number(state.desiredQuantity)) && Number(state.desiredQuantity) !== Number(state.confirmedQuantity)) {
        state.needsResync = true;
        processQuantityUpdateState(state);
      } else {
        const itemCard = (state.lineKey
          ? Array.from(sidebarRoot?.querySelectorAll('.waycel-cart-sidebar__item') || []).find((row) => row.dataset.lineKey === state.lineKey)
          : null) ||
          sidebarRoot?.querySelector(`.waycel-cart-sidebar__item[data-line="${state.line}"]`);
        if (state.confirmedQuantity != null && Number.isFinite(Number(state.confirmedQuantity))) {
          setVisibleLineQuantity(itemCard, Number(state.confirmedQuantity));
        }
        setCartLineQuantityPending(itemCard, false);
        clearQuantityUpdateState(state);
        updateCheckoutVariantValidation();
      }
    }
  }

  async function updateLineQuantity(line, quantity, lineKey = '') {
    if (!line && !lineKey) return;
    const state = getQuantityUpdateState(line, lineKey);
    if (!state) return;
    state.desiredQuantity = Math.max(Number(quantity || 0), 0);
    state.needsResync = true;
    return processQuantityUpdateState(state);
  }

  function isSidebarOpen() {
    return Boolean(sidebarRoot?.classList.contains('active') || sidebarRoot?.classList.contains('waycel-is-open'));
  }

  function flushLineQuantityUpdate(line, lineKey = '', quantity = 1) {
    const state = getQuantityUpdateState(line, lineKey);
    if (!state) return undefined;
    state.desiredQuantity = Math.max(Number(quantity || 0), 0);
    return processQuantityUpdateState(state);
  }

  function scheduleLineQuantityUpdate(line, lineKey = '', quantity = 1) {
    const state = getQuantityUpdateState(line, lineKey);
    if (!state) return undefined;
    state.desiredQuantity = Math.max(Number(quantity || 0), 0);
    state.needsResync = true;
    if (state.debounceTimer) window.clearTimeout(state.debounceTimer);
    if (!state.isUpdating) {
      state.debounceTimer = window.setTimeout(() => {
        state.debounceTimer = 0;
        processQuantityUpdateState(state);
      }, QUANTITY_UPDATE_DEBOUNCE_MS);
    }
    activeQuantityUpdates.add(state.key);
    updateCheckoutVariantValidation();
    return state.promise;
  }

  function handleQuantityButton(quantityButton) {
    const line = Number(quantityButton.dataset.line);
    const lineKey = getLineKeyFromElement(quantityButton);
    if (!line && !lineKey) return;
    const itemCard = quantityButton.closest('.waycel-cart-sidebar__item');
    const quantityElement = itemCard?.querySelector('.waycel-cart-sidebar__quantity-value');
    const displayedQty = Number(quantityElement?.textContent?.trim() || 0);
    const currentQty = displayedQty;
    if (quantityButton.dataset.waycelQtyButton === 'minus' && currentQty <= 1) {
      updateQuantityMinusButtonState(itemCard, currentQty);
      return;
    }
    const nextQty = quantityButton.dataset.waycelQtyButton === 'minus' ? Math.max(currentQty - 1, 0) : currentQty + 1;
    const state = getQuantityUpdateState(line, lineKey);
    if (state && state.confirmedQuantity == null) {
      state.confirmedQuantity = currentQty;
    }
    setVisibleLineQuantity(itemCard, nextQty);
    setCartLineQuantityPending(itemCard, true);
    scheduleLineQuantityUpdate(line, lineKey, nextQty);
  }

  function getRecommendationAddButtons() {
    return Array.from(sidebarRoot?.querySelectorAll('[data-waycel-recommendation-add]') || []);
  }

  function setRecommendationAddButtonsDisabled(isDisabled, activeButton = null) {
    getRecommendationAddButtons().forEach((button) => {
      if (!button.dataset.defaultText) {
        button.dataset.defaultText = button.textContent.trim();
      }

      if (isDisabled) {
        if (!button.disabled) {
          button.dataset.waycelRecommendationLockDisabled = 'true';
          button.disabled = true;
        }
        if (button === activeButton) {
          button.textContent = 'Adding';
        }
        return;
      }

      if (button.dataset.waycelRecommendationLockDisabled === 'true') {
        button.disabled = false;
        delete button.dataset.waycelRecommendationLockDisabled;
      }
      button.textContent = button.dataset.defaultText || 'Add';
    });
  }

  async function waitForLineQuantityStability(line, lineKey = '') {
    const pendingQuantityKey = getPendingQuantityKey(line, lineKey);
    if (!pendingQuantityKey) return;

    let attempts = 0;
    while ((activeQuantityUpdates.has(pendingQuantityKey) || isQuantityStatePending(getExistingQuantityUpdateState(line, lineKey))) && attempts < 120) {
      attempts += 1;
      await sleep(25);
    }
  }

  async function waitForAllQuantityUpdates() {
    let attempts = 0;
    while (hasPendingQuantityUpdates() && attempts < 160) {
      attempts += 1;
      await sleep(25);
    }
  }

  async function handleRecommendationAdd(addButton) {
    const variantId = Number(addButton.dataset.variantId);
    if (!variantId || addButton.dataset.isSubmitting === 'true' || isRecommendationAdding) return;
    isRecommendationAdding = true;
    addButton.dataset.isSubmitting = 'true';
    setRecommendationAddButtonsDisabled(true, addButton);
    try {
      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('Recommendation add failed');
      await refreshCart({ updateItemsFromSection: true });
      openSidebar(document.activeElement);
    } catch (error) {
      console.error('Waycel cart sidebar recommendation add failed', error);
      addButton.textContent = 'Try again';
    } finally {
      isRecommendationAdding = false;
      setRecommendationAddButtonsDisabled(false);
      delete addButton.dataset.isSubmitting;
    }
  }

  function setDiscountFeedback(discountBlock, message, color) {
    const feedback = discountBlock?.querySelector('.waycel-cart-discount__feedback');
    if (!feedback) return;
    feedback.textContent = message;
    feedback.style.color = color;
  }

  function discountTitleMatchesCode(discount, code) {
    const normalizedCode = String(code || '').trim().toLowerCase();
    const candidates = [discount?.title, discount?.code, discount?.discount_application?.title, discount?.discount_application?.code];
    return candidates.some((candidate) => String(candidate || '').trim().toLowerCase() === normalizedCode);
  }

  function cartHasAppliedDiscountCode(cart, code) {
    if (!cart || !code) return false;
    const cartLevelDiscounts = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.discount_applications || []),
    ];
    if (cartLevelDiscounts.some((discount) => discountTitleMatchesCode(discount, code))) return true;

    return (cart.items || []).some((item) => {
      const lineDiscounts = [
        ...(item.line_level_discount_allocations || []),
        ...(item.discount_allocations || []),
        ...(item.discounts || []),
      ];
      return lineDiscounts.some((discount) => discountTitleMatchesCode(discount, code));
    });
  }

  async function handleDiscountApply(applyButton) {
    const discountBlock = applyButton.closest('.waycel-cart-discount');
    const input = discountBlock?.querySelector('.waycel-cart-discount__input');
    const code = input?.value?.trim() || '';
    if (!code) {
      setDiscountFeedback(discountBlock, 'Please enter a code', '#e53935');
      return;
    }

    const originalText = applyButton.textContent;
    applyButton.disabled = true;
    applyButton.textContent = 'Applying...';
    setDiscountFeedback(discountBlock, '', '#e53935');

    try {
      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      if (!response.ok) throw new Error('Discount update failed');
      const updatedCart = await response.json();
      await refreshCart({ updateItemsFromSection: true });
      const refreshedCart = await fetchCartJson().catch(() => updatedCart);
      if (cartHasAppliedDiscountCode(refreshedCart, code)) {
        setDiscountFeedback(discountBlock, 'Discount applied', '#2e7d32');
      } else {
        setDiscountFeedback(discountBlock, 'Invalid or expired code', '#e53935');
      }
    } catch (error) {
      console.error('Waycel cart sidebar discount apply failed', error);
      setDiscountFeedback(discountBlock, 'Invalid or expired code', '#e53935');
    } finally {
      applyButton.disabled = false;
      applyButton.textContent = originalText;
    }
  }

  async function handleDiscountRemove(removeButton) {
    if (removeButton.dataset.isSubmitting === 'true') return;
    const discountBlock = sidebarRoot?.querySelector('.waycel-cart-discount');
    removeButton.dataset.isSubmitting = 'true';
    removeButton.disabled = true;
    setDiscountFeedback(discountBlock, '', '#e53935');

    try {
      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
      if (!response.ok) throw new Error('Discount remove failed');
      await refreshCart({ updateItemsFromSection: true });
    } catch (error) {
      console.error('Waycel cart sidebar discount remove failed', error);
      setDiscountFeedback(discountBlock, 'Discount could not be removed', '#e53935');
    } finally {
      removeButton.disabled = false;
      delete removeButton.dataset.isSubmitting;
    }
  }

  async function persistCurrentLineVariantConfirmation(lineKey, line, variantId, currentItem) {
    let cart = null;
    if (!currentItem) {
      cart = await fetchCartJson();
      currentItem = lineKey ? cart?.items?.find((item) => item.key === lineKey) : getCartItemByLine(cart, line);
    }
    if (!currentItem) throw new Error('Cart line not found');
    if (!cart && lineKey && !line) {
      cart = await fetchCartJson();
    }
    const resolvedLine = line || (lineKey ? findCartLineByKey(cart, lineKey) : 0);
    if (!resolvedLine) throw new Error('Cart line for variant confirmation not found');
    const response = await fetch(getCartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: resolvedLine,
        quantity: currentItem.quantity,
        properties: buildConfirmedVariantProperties(currentItem, variantId),
      }),
    });
    if (!response.ok) throw new Error('Variant confirmation failed');
    return response.json();
  }

  async function removeCartLineItemBySnapshot(snapshot) {
    const cart = await fetchCartJson();
    const line = findCartLineBySnapshot(cart, snapshot);
    if (!line) throw new Error('Cart line to remove not found');
    const response = await fetch(getCartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity: 0 }),
    });
    if (!response.ok) throw new Error('Variant remove failed');
    return response.json();
  }

  async function handleVariantChange(variantSelect) {
    const line = Number(variantSelect.dataset.line);
    const nextVariantId = Number(variantSelect.value);
    const currentVariantId = Number(variantSelect.dataset.currentVariantId);
    const lineKey = getVariantSelectLineKey(variantSelect);
    const itemCard = variantSelect.closest('.waycel-cart-sidebar__item');
    if (!line || !nextVariantId || variantSelect.dataset.isSubmitting === 'true' || isVariantUpdating) return;
    const visualIndex = getCartItemVisualIndex(itemCard);
    const visibleQuantity = getVisibleLineQuantity(itemCard, Number(variantSelect.dataset.quantity || 1));
    const previousValue = String(currentVariantId);
    const wasConfirmed = variantSelect.dataset.waycelVariantConfirmed === 'true';
    const scrollState = getCartScrollState();
    isVariantUpdating = true;
    variantSelect.dataset.isSubmitting = 'true';
    setCartLineControlsDisabled(itemCard, true);
    itemCard?.classList.add('waycel-cart-sidebar__item--updating');
    try {
      await waitForLineQuantityStability(line, lineKey);
      isInternalCartMutation = true;
      const cart = await fetchCartJson();
      const currentItem = getCartItemForVariantSelect(cart, line, variantSelect);
      if (!currentItem) throw new Error('Cart line not found');
      const lineItemKey = currentItem.key;
      const currentLine = findCartLineByKey(cart, lineItemKey);
      const originalLineSnapshot = createCartLineSnapshot(currentItem, currentLine);
      const quantity = Math.max(Number(currentItem.quantity || visibleQuantity || 1), 1);
      let updatedCart = null;
      if (!lineItemKey) throw new Error('Cart line key not found');
      if (!currentLine) throw new Error('Current cart line not found');
      if (!originalLineSnapshot) throw new Error('Cart line snapshot not found');
      if (itemCard) itemCard.dataset.lineKey = lineItemKey;
      variantSelect.dataset.lineKey = lineItemKey;

      if (nextVariantId === Number(currentItem.variant_id)) {
        currentItem.quantity = quantity;
        updatedCart = await persistCurrentLineVariantConfirmation(lineItemKey, currentLine, nextVariantId, currentItem);
      } else {
        const nextItem = {
          id: nextVariantId,
          quantity,
          properties: buildConfirmedVariantProperties(currentItem, nextVariantId),
        };
        const sellingPlanId = currentItem.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlanId) nextItem.selling_plan = sellingPlanId;

        const addResponse = await fetch(getCartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [nextItem] }),
        });
        if (!addResponse.ok) throw new Error('Variant add failed');

        updatedCart = await removeCartLineItemBySnapshot(originalLineSnapshot);
      }

      markVariantSelectConfirmed(variantSelect, nextVariantId);
      await refreshCart({ preserveScroll: true, updateItemsFromSection: true });
      restoreCartScrollState(scrollState);
      openSidebar(document.activeElement);
      updateCheckoutVariantValidation();
    } catch (error) {
      console.error('Waycel cart sidebar variant change failed', error);
      variantSelect.value = previousValue;
      if (wasConfirmed) {
        markVariantSelectConfirmed(variantSelect, previousValue);
      } else {
        markVariantSelectNeedsConfirmation(variantSelect);
      }
      updateCheckoutVariantValidation();
    } finally {
      isInternalCartMutation = false;
      isVariantUpdating = false;
      delete variantSelect.dataset.isSubmitting;
      setCartLineControlsDisabled(itemCard, false);
      itemCard?.classList.remove('waycel-cart-sidebar__item--updating');
    }
  }

  function openSizeChartModal() {
    if (!sidebarRoot) return;
    const modal = sidebarRoot.querySelector('[data-waycel-size-chart-modal]');
    if (!modal) return;
    const image = modal.querySelector('[data-waycel-size-chart-image]');
    const chartSrc = getArmSleeveSizeChartSrc();
    if (image && chartSrc && image.getAttribute('src') !== chartSrc) {
      image.setAttribute('src', chartSrc);
    }
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => modal.classList.add('is-open'));
  }

  function closeSizeChartModal() {
    const modal = sidebarRoot?.querySelector('[data-waycel-size-chart-modal]');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      if (!modal.classList.contains('is-open')) modal.hidden = true;
    }, 180);
  }

  function bindDocumentQuantityEvents() {
    if (documentQuantityEventsBound) return;
    documentQuantityEventsBound = true;
    document.addEventListener('click', (event) => {
      const quantityButton = event.target.closest('[data-waycel-qty-button]');
      if (!quantityButton) return;
      const drawerRoot = quantityButton.closest('cart-drawer.waycel-cart-sidebar-host');
      if (!drawerRoot) return;
      sidebarRoot = drawerRoot;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleQuantityButton(quantityButton);
    }, true);
  }

  function bindCartTriggerEvents() {
    if (cartTriggerEventsBound) return;
    cartTriggerEventsBound = true;
    document.addEventListener('click', (event) => {
      const cartTrigger = event.target.closest('[data-waycel-cart-trigger]');
      if (!cartTrigger) return;
      const cartDrawer = document.querySelector('cart-drawer.waycel-cart-sidebar-host') || document.querySelector('cart-drawer');
      if (!cartDrawer) return;
      event.preventDefault();
      event.stopPropagation();
      sidebarRoot = cartDrawer;
      refreshCart({ updateItemsFromSection: true }).finally(() => {
        openSidebar(cartTrigger);
      });
    }, true);
  }

  function resetSwipeState() {
    swipeStartX = 0;
    swipeStartY = 0;
    swipeCurrentX = 0;
    swipeTracking = false;
    swipeHorizontal = false;
  }

  function getSwipeSurface() {
    return sidebarRoot?.querySelector('.waycel-cart-sidebar__inner');
  }

  function isSizeChartModalOpen() {
    return Boolean(sidebarRoot?.querySelector('[data-waycel-size-chart-modal].is-open'));
  }

  function clearSwipeTransform() {
    const swipeSurface = getSwipeSurface();
    if (!swipeSurface) return;
    swipeSurface.style.transition = '';
    swipeSurface.style.transform = '';
  }

  function animateSwipeBack() {
    const swipeSurface = getSwipeSurface();
    if (!swipeSurface) return;
    swipeSurface.style.transition = 'transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)';
    swipeSurface.style.transform = 'translateX(0)';
    window.setTimeout(() => { clearSwipeTransform(); }, 230);
  }

  function bindSwipeCloseEvents() {
    if (!sidebarRoot || swipeEventsBound) return;
    swipeEventsBound = true;
    const swipeSurface = getSwipeSurface();
    if (!swipeSurface) return;

    swipeSurface.addEventListener('touchstart', (event) => {
      if (isDesktop() || !isSidebarOpen() || isSizeChartModalOpen()) return;
      if (!event.touches || event.touches.length !== 1) return;
      swipeStartX = event.touches[0].clientX;
      swipeStartY = event.touches[0].clientY;
      swipeCurrentX = swipeStartX;
      swipeTracking = true;
      swipeHorizontal = false;
    }, { passive: true });

    swipeSurface.addEventListener('touchmove', (event) => {
      if (!swipeTracking || isDesktop() || !isSidebarOpen()) return;
      if (!event.touches || event.touches.length !== 1) return;
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      const deltaX = currentX - swipeStartX;
      const deltaY = currentY - swipeStartY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (!swipeHorizontal) {
        if (absX < 10 && absY < 10) return;
        if (deltaX > 0 && absX > absY * 1.35) {
          swipeHorizontal = true;
        } else if (absY > absX) {
          resetSwipeState();
          return;
        } else {
          return;
        }
      }
      if (deltaX <= 0) return;
      event.preventDefault();
      swipeCurrentX = currentX;
      const swipeDistance = Math.min(deltaX, swipeSurface.offsetWidth);
      swipeSurface.style.transition = 'none';
      swipeSurface.style.transform = `translateX(${swipeDistance}px)`;
    }, { passive: false });

    swipeSurface.addEventListener('touchend', () => {
      if (!swipeTracking || isDesktop() || !isSidebarOpen()) { resetSwipeState(); return; }
      const deltaX = swipeCurrentX - swipeStartX;
      const swipeSurfaceWidth = getSwipeSurface()?.offsetWidth || 0;
      const closeDistance = Math.max(SWIPE_CLOSE_MIN_DISTANCE, swipeSurfaceWidth * SWIPE_CLOSE_DISTANCE_RATIO);
      if (swipeHorizontal && deltaX > closeDistance) {
        clearSwipeTransform();
        forceCloseSidebar();
      } else if (swipeHorizontal) {
        animateSwipeBack();
      }
      resetSwipeState();
    });

    swipeSurface.addEventListener('touchcancel', () => {
      if (swipeHorizontal) animateSwipeBack();
      resetSwipeState();
    });
  }

  function patchFetch() {
    if (fetchPatched || typeof window.fetch !== 'function') return;
    fetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async function (...args) {
      const requestTarget = args[0];
      const requestInit = args[1] || {};
      const requestUrl = typeof requestTarget === 'string' ? requestTarget : requestTarget?.url || '';
      const method = (requestInit.method || requestTarget?.method || 'GET').toUpperCase();
      const requestHeaders = requestInit.headers || requestTarget?.headers;
      const requestSource = getHeaderValue(requestHeaders, 'X-Waycel-Cart-Source');
      const isCartAdd = requestUrl.includes('/cart/add.js') && method === 'POST';
      const response = await originalFetch(...args);
      if (isCartAdd && response.ok && !isInternalCartMutation && requestSource !== 'product-form') {
        Promise.resolve().then(async () => {
          await refreshCart({ updateItemsFromSection: true });
          openSidebar(document.activeElement);
        });
      }
      return response;
    };
  }

  function patchDrawerMethods() {
    if (!sidebarRoot || sidebarRoot.dataset.waycelPatched === 'true') return;
    sidebarRoot.dataset.waycelPatched = 'true';
    const originalRenderContents = typeof sidebarRoot.renderContents === 'function' ? sidebarRoot.renderContents.bind(sidebarRoot) : null;
    sidebarRoot.open = function (triggeredBy) { openSidebar(triggeredBy); };
    sidebarRoot.close = function (options = {}) { if (!options.force) return; forceCloseSidebar(); };
    sidebarRoot.renderContents = function (parsedState) {
      const acceleratedCheckoutElements = getPreservableAcceleratedCheckoutElements();
      if (originalRenderContents) originalRenderContents(parsedState);
      requestAnimationFrame(async () => {
        sidebarRoot = document.querySelector('cart-drawer');
        restoreAcceleratedCheckoutElements(acceleratedCheckoutElements);
        swipeEventsBound = false;
        bindDelegatedEvents();
        bindSwipeCloseEvents();
        patchDrawerMethods();
        if (hasCartDrawerSectionHtml(parsedState)) {
          applyCartDrawerSectionHtml(getCartDrawerSectionHtml(parsedState), {
            updateItems: true,
            cartItems: parsedState?.items,
            updateRecommendations: true,
            updateCtas: true,
          });
          refreshCartIconBubble().catch(() => undefined);
        } else {
          await refreshCart({ updateItemsFromSection: true });
        }
        openSidebar(document.activeElement);
      });
    };
  }

  function bindDelegatedEvents() {
    if (!sidebarRoot || sidebarRoot.dataset.waycelEventsBound === 'true') return;
    sidebarRoot.dataset.waycelEventsBound = 'true';

    sidebarRoot.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-waycel-cart-close]');
      if (closeButton) { event.preventDefault(); forceCloseSidebar(); return; }

      const overlay = event.target.closest('#CartDrawer-Overlay');
      if (overlay) {
        event.preventDefault();
        event.stopImmediatePropagation();
        forceCloseSidebar();
        return;
      }

      const moreOptionsTrigger = event.target.closest('[data-waycel-more-payment-options]');
      if (moreOptionsTrigger) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (hasPendingQuantityUpdates()) {
          updateCheckoutVariantValidation();
          return;
        }
        const panel = sidebarRoot.querySelector('[data-waycel-payment-options-panel]');
        if (!panel) { console.warn('Waycel cart: payment panel not found'); return; }
        const isOpen = panel.classList.contains('is-open');
        setPaymentOptionsExpanded(moreOptionsTrigger, !isOpen);
        return;
      }

      const removeButton = event.target.closest('[data-waycel-remove]');
      if (removeButton) {
        event.preventDefault();
        if (isQuantityStatePending(getExistingQuantityUpdateState(Number(removeButton.dataset.line), getLineKeyFromElement(removeButton)))) return;
        updateLineQuantity(Number(removeButton.dataset.line), 0, getLineKeyFromElement(removeButton));
        return;
      }

      const recommendationAddButton = event.target.closest('[data-waycel-recommendation-add]');
      if (recommendationAddButton) { event.preventDefault(); handleRecommendationAdd(recommendationAddButton); return; }

      const discountApplyButton = event.target.closest('.waycel-cart-discount__apply');
      if (discountApplyButton) { event.preventDefault(); handleDiscountApply(discountApplyButton); return; }

      const discountRemoveButton = event.target.closest('[data-waycel-remove-discount]');
      if (discountRemoveButton) { event.preventDefault(); handleDiscountRemove(discountRemoveButton); return; }

      const sizeChartOpenButton = event.target.closest('[data-waycel-size-chart-open]');
      if (sizeChartOpenButton) { event.preventDefault(); openSizeChartModal(); return; }

      const sizeChartCloseButton = event.target.closest('[data-waycel-size-chart-close]');
      if (sizeChartCloseButton) { event.preventDefault(); closeSizeChartModal(); return; }

      const sizeChartModal = event.target.closest('[data-waycel-size-chart-modal]');
      if (sizeChartModal && event.target === sizeChartModal) { event.preventDefault(); closeSizeChartModal(); return; }

      const quantityButton = event.target.closest('[data-waycel-qty-button]');
      if (quantityButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        handleQuantityButton(quantityButton);
      }
    }, true);

    sidebarRoot.addEventListener('change', (event) => {
      const variantSelect = event.target.closest('[data-waycel-variant-select]');
      if (!variantSelect) return;
      event.preventDefault();
      handleVariantChange(variantSelect);
    }, true);

    sidebarRoot.addEventListener('submit', async (event) => {
      const checkoutForm = event.target.closest('[data-waycel-checkout-form]');
      if (!checkoutForm) return;
      if (checkoutForm.dataset.waycelAllowSyncedSubmit === 'true') {
        delete checkoutForm.dataset.waycelAllowSyncedSubmit;
        return;
      }
      if (hasPendingQuantityUpdates()) {
        event.preventDefault();
        updateCheckoutVariantValidation();
        await waitForAllQuantityUpdates();
        if (!updateCheckoutVariantValidation()) return;
        checkoutForm.dataset.waycelAllowSyncedSubmit = 'true';
        const checkoutButton = sidebarRoot.querySelector('[data-waycel-checkout-button]');
        if (typeof checkoutForm.requestSubmit === 'function' && checkoutButton) {
          checkoutForm.requestSubmit(checkoutButton);
        } else {
          checkoutForm.submit();
        }
        return;
      }
      if (!updateCheckoutVariantValidation()) {
        event.preventDefault();
        const firstUnconfirmedVariant = getVariantSelects().find((s) => s.dataset.waycelVariantConfirmed !== 'true');
        firstUnconfirmedVariant?.closest('.waycel-cart-sidebar__variant-control')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);

    sidebarRoot.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') {
        event.preventDefault();
        const sizeChartModal = sidebarRoot.querySelector('[data-waycel-size-chart-modal]');
        if (sizeChartModal?.classList.contains('is-open')) { closeSizeChartModal(); event.stopImmediatePropagation(); return; }
        event.stopImmediatePropagation();
      }
    }, true);
  }

  function subscribeToCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.source === 'product-form') return;
      await refreshCart({ updateItemsFromSection: true });
      if (event?.source && event.source !== 'cart-items') {
        openSidebar(document.activeElement);
      } else if (getStoredOpenState()) {
        updateOpenClasses(true);
      }
    });
  }

  function restoreOpenState() {
    if (getStoredOpenState()) {
      openSidebar();
    } else {
      updateOpenClasses(false);
    }
  }

  function initWaycelCartSidebar() {
    sidebarRoot = document.querySelector('cart-drawer');
    setViewportHeightProperty();
    if (!sidebarRoot || isInitialized) return;
    isInitialized = true;
    patchFetch();
    patchDrawerMethods();
    bindDelegatedEvents();
    bindDocumentQuantityEvents();
    bindCartTriggerEvents();
    bindSwipeCloseEvents();
    subscribeToCartEvents();
    refreshCart({ updateItemsFromSection: true }).finally(() => {
      updateCheckoutVariantValidation();
      restoreOpenState();
    });
    window.addEventListener('resize', () => {
      scheduleViewportHeightUpdate();
      if (getStoredOpenState()) { updateOpenClasses(true); } else { updateOpenClasses(false); }
    });
    window.addEventListener('orientationchange', scheduleViewportHeightUpdate);
    window.visualViewport?.addEventListener('resize', scheduleViewportHeightUpdate);
    window.visualViewport?.addEventListener('scroll', scheduleViewportHeightUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWaycelCartSidebar, { once: true });
  } else {
    initWaycelCartSidebar();
  }
})();
